import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import LabeledFrame from 'components/LabeledFrame';
import { downloadFile } from 'helpers/generic';
import { getAjaxUrl } from 'helpers/config';

let axiosCancelToken = null;

const AttachmentsStyled = styled.div`
    .attachment-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 10px;
        background: var(--bs-gray-100);
        border-radius: 5px;
        margin-bottom: 5px;
        cursor: pointer;
        transition: all 0.2s;

        &:last-child {
            margin-bottom: 0;
        }

        &:hover {
            background: var(--bs-gray-200);
        }

        &.no-clickable {
            cursor: default;

            &:hover {
                background: var(--bs-gray-100);
            }
        }

        .attachment-name {
            font-size: 13px;
            color: var(--bs-gray-700);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;

            i {
                color: var(--bs-danger);
                margin-right: 5px;
            }
        }

        .attachment-remove {
            cursor: pointer;
            color: var(--bs-danger);
            font-size: 14px;
            margin-left: 10px;

            &:hover {
                color: var(--bs-dark);
            }
        }
    }

    .no-attachments {
        padding: 10px;
        text-align: center;
        color: var(--bs-gray-500);
        font-size: 13px;
    }

    .upload-zone {
        border: 2px dashed var(--bs-gray-300);
        border-radius: 5px;
        padding: 20px;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            border-color: var(--bs-primary);
            background: var(--bs-gray-50);
        }

        input[type="file"] {
            display: none;
        }

        .upload-text {
            color: var(--bs-gray-600);
            font-size: 13px;

            i {
                font-size: 24px;
                display: block;
                margin-bottom: 10px;
                color: var(--bs-primary);
            }

            small {
                display: block;
                margin-top: 5px;
                color: var(--bs-gray-500);
                font-size: 11px;
            }
        }
    }
`;

export default function Attachments(props) {
    const data = props.data;
    const setData = props.setData;
    const errors = props.errors;

    const [queryParams] = useSearchParams();
    const attach_file_id = queryParams.get('attach_file_id') ?? null;

    const [filesToUploadPreview, setFilesToUploadPreview] = useState([]);

    useEffect(() => {
        axiosCancelToken = axios.CancelToken.source();

        return function cleanup() {
            axiosCancelToken.cancel();
        }
    }, []);

    useEffect(() => {
        if ( !attach_file_id ) return;

        axios.get('/api/manager/documents/get-file/' + attach_file_id, {
            cancelToken: axiosCancelToken.token
        }).then((response) => {
            let file = response.data;

            setData((prev) => ({
                ...prev,
                attach_file_id: attach_file_id,
                attachments: [...(prev.attachments ?? []), {name: file.name}]
            }));
        }).catch((error) => {
            if ( axios.isCancel(error) ) return;
        });
    }, [attach_file_id, setData]);

    useEffect(() => {
        // Actualizar preview cuando cambian los archivos
        if (data.filesToUpload && data.filesToUpload.length > 0) {
            const previews = Array.from(data.filesToUpload).map(file => ({
                name: file.name,
                size: file.size
            }));
            setFilesToUploadPreview(previews);
        } else {
            setFilesToUploadPreview([]);
        }
    }, [data.filesToUpload]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);

        // Validar que sean solo PDFs
        const pdfFiles = files.filter(file => file.type === 'application/pdf');

        if (pdfFiles.length !== files.length) {
            alert('Solo se permiten archivos PDF');
        }

        if (pdfFiles.length > 0) {
            // Agregar a los archivos existentes
            const existingFiles = data.filesToUpload ? Array.from(data.filesToUpload) : [];
            const allFiles = [...existingFiles, ...pdfFiles];

            setData((prev) => ({
                ...prev,
                filesToUpload: allFiles
            }));
        }

        // Reset input
        e.target.value = null;
    }

    const removeAttachment = (idx) => {
        let remove_attachments = data.remove_attachments ?? [];
        remove_attachments.push(idx);

        setData((prev) => ({
            ...prev,
            remove_attachments: remove_attachments
        }));
    }

    const removeFileToUpload = (idx) => {
        let filesToUpload = data.filesToUpload ? Array.from(data.filesToUpload) : [];
        filesToUpload.splice(idx, 1);

        setData((prev) => ({
            ...prev,
            filesToUpload: filesToUpload
        }));
    }

    const downloadAttachment = (idx) => {
        if (!data.id) return; // No se puede descargar si no está guardado
        downloadFile(getAjaxUrl() + 'api/manager/postal-mails/download-attachment/' + data.id + '/' + idx + '?community_id=' + data.community_id);
    }

    return (
        <AttachmentsStyled>
            <LabeledFrame label="Adjuntos PDF">
                <div className="mb-2">
                    {/* Archivos ya subidos */}
                    {data.attachments && data.attachments.length > 0 && (
                        <div className="mb-3">
                            <small className="text-muted d-block mb-2">Archivos adjuntos:</small>
                            {data.attachments.map((attachment, idx) => {
                                // Verificar si está marcado para eliminar
                                if (data.remove_attachments && data.remove_attachments.includes(idx)) {
                                    return null;
                                }

                                return (
                                    <div
                                        className={`attachment-item ${!data.id ? 'no-clickable' : ''}`}
                                        key={idx}
                                        onClick={() => data.id ? downloadAttachment(idx) : null}
                                    >
                                        <div className="attachment-name">
                                            <i className="bi bi-file-pdf"></i>
                                            {attachment.name}
                                        </div>
                                        <div className="attachment-remove" onClick={(e) => {
                                            e.stopPropagation();
                                            removeAttachment(idx);
                                        }}>
                                            <i className="bi bi-x-circle"></i>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Mensaje "No hay adjuntos" */}
                    {(!data.attachments || data.attachments.length === 0 || (data.remove_attachments && data.remove_attachments.length === data.attachments.length)) && filesToUploadPreview.length === 0 && (
                        <div className="no-attachments">
                            No hay adjuntos
                        </div>
                    )}

                    {/* Archivos pendientes de subir */}
                    {filesToUploadPreview.length > 0 && (
                        <div className="mb-3">
                            <small className="text-muted d-block mb-2">Archivos pendientes de subir:</small>
                            {filesToUploadPreview.map((file, idx) => (
                                <div className="attachment-item" key={idx}>
                                    <div className="attachment-name">
                                        <i className="bi bi-file-pdf"></i>
                                        {file.name}
                                    </div>
                                    <div className="attachment-remove" onClick={() => removeFileToUpload(idx)}>
                                        <i className="bi bi-x-circle"></i>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Zona de upload */}
                    <div className="upload-zone" onClick={() => document.getElementById('file-upload').click()}>
                        <input
                            id="file-upload"
                            type="file"
                            multiple
                            accept="application/pdf"
                            onChange={handleFileChange}
                        />
                        <div className="upload-text">
                            <i className="bi bi-cloud-upload"></i>
                            Click para seleccionar archivos PDF
                            <small>Solo se aceptan archivos PDF (máximo 10MB cada uno)</small>
                        </div>
                    </div>

                    {errors.filesToUpload && (
                        <div className="invalid-feedback d-block mt-2">{errors.filesToUpload[0]}</div>
                    )}
                </div>
            </LabeledFrame>
        </AttachmentsStyled>
    );
}
